import axios from 'axios'

import { analyticsTypeToJSON, exchangeToJSON } from '@/types/generated/common/types'
import {
  type ChartIntegrity,
  type ChartReference,
  type ChartSeries,
  ChartType,
  Citation,
  ReportBlock,
  ReportMeta,
  ResearchJobEvent,
  type WidgetContext,
} from '@/types/generated/research'

// Lazily imported so this module (and its pure parsers) stays importable in unit tests
// without pulling in env.ts's eager validation, which throws when NEXT_PUBLIC_* vars are
// unset (as they are under the plain `bun test` runner — no .env.test file exists).
async function getAiClient() {
  const [{ orderXAiClient }, { env }] = await Promise.all([
    import('@/service/ai-client'),
    import('@/env'),
  ])
  return { orderXAiClient, isDevEnv: env.NEXT_PUBLIC_ENV === 'dev' }
}

export type ResearchJobPhase =
  | 'PENDING'
  | 'RUNNING'
  | 'DELIVERED'
  | 'FAILED'
  | 'KILLED'
  | 'CANCELLED'

export interface ResearchJobCost {
  inputTokens: number
  outputTokens: number
  toolCalls: number
}

export type ParsedReportBlock =
  | { kind: 'markdown'; section: string; content: string; sources: string[] }
  | {
      kind: 'chart'
      section: string
      caption: string
      chartType: ChartType
      // Optional to match the generated ChartBlock type exactly — reference/integrity are
      // `X | undefined` there (proto3 message fields), not guaranteed present.
      reference?: ChartReference
      integrity?: ChartIntegrity
      series: ChartSeries[]
      aggregates: Record<string, string>
      sources: string[]
      isPreview: boolean
      truncated: boolean
    }

export interface ParsedReport {
  meta: ReportMeta
  blocks: ParsedReportBlock[]
  citations: Citation[]
}

export type ResearchPollResult =
  | {
      phase: 'PENDING' | 'RUNNING'
      jobId: string
      cost: ResearchJobCost
      events: ResearchJobEvent[]
    }
  | {
      phase: 'DELIVERED'
      jobId: string
      cost: ResearchJobCost
      events: ResearchJobEvent[]
      report: ParsedReport
    }
  | {
      phase: 'FAILED' | 'KILLED' | 'CANCELLED'
      jobId: string
      cost: ResearchJobCost
      events: ResearchJobEvent[]
      error: string | null
    }

export type ResearchApiErrorKind =
  | 'validation'
  | 'business'
  | 'auth'
  | 'upstream'
  | 'server'
  | 'not_found'
  | 'unknown'

export interface ResearchApiErrorIssue {
  loc: (string | number)[]
  msg: string
  type: string
}

export interface ResearchApiErrorShape {
  kind: ResearchApiErrorKind
  message: string
  issues?: ResearchApiErrorIssue[]
}

// Thrown by postExplainWidget/getResearchReport so callers (mutation.error / query.error)
// always get one consistent, user-safe-to-render shape regardless of which HTTP failure
// mode produced it (422 validation list, 422 business string, 401/404/500/502 string, or a
// network/unknown failure).
export class ResearchApiError extends Error implements ResearchApiErrorShape {
  kind: ResearchApiErrorKind
  issues?: ResearchApiErrorIssue[]

  constructor(shape: ResearchApiErrorShape) {
    super(shape.message)
    this.name = 'ResearchApiError'
    this.kind = shape.kind
    this.issues = shape.issues
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

// Isolated per §4 of the confirmed contract: POST /research/widget can 422 in two
// genuinely different shapes — FastAPI/pydantic auto-validation (`detail` is a list of
// {loc,msg,type}) vs. a manual business-rule rejection (`detail` is a plain string, already
// user-safe copy meant to be rendered verbatim). 401/404/500/502 also carry a plain string
// `detail`. Anything else (network failure, unexpected shape) falls back to a generic,
// user-safe message.
export function parseResearchApiError(error: unknown): ResearchApiError {
  if (!axios.isAxiosError(error)) {
    return new ResearchApiError({
      kind: 'unknown',
      message: 'Something went wrong. Please try again.',
    })
  }

  const status = error.response?.status
  const data = error.response?.data
  const detail = isRecord(data) ? data.detail : undefined

  if (status === 422) {
    if (Array.isArray(detail)) {
      const issues: ResearchApiErrorIssue[] = detail.map((issue) => ({
        loc: isRecord(issue) && Array.isArray(issue.loc) ? issue.loc : [],
        msg: isRecord(issue) && typeof issue.msg === 'string' ? issue.msg : 'Invalid value',
        type: isRecord(issue) && typeof issue.type === 'string' ? issue.type : 'unknown',
      }))
      return new ResearchApiError({
        kind: 'validation',
        message: issues[0]?.msg ?? 'Invalid request',
        issues,
      })
    }
    if (typeof detail === 'string') {
      return new ResearchApiError({ kind: 'business', message: detail })
    }
  }

  // Proposal-aware /research and /research/{id}/followup 409 when the proposal_id they were
  // given has expired or was already used (see the "composed-hugging-hammock" plan, blocker
  // #3 — exact {code, detail} shape unconfirmed). Treated as a business-rule rejection: real
  // server copy when present, a generic-but-accurate fallback otherwise, so the proposal card
  // always has something renderable instead of throwing "Something went wrong."
  if (status === 409) {
    const message =
      typeof detail === 'string'
        ? detail
        : 'This research proposal has expired or was already used — please ask again.'
    return new ResearchApiError({ kind: 'business', message })
  }

  if (status === 404 && typeof detail === 'string') {
    return new ResearchApiError({ kind: 'not_found', message: detail })
  }
  if (status === 401 && typeof detail === 'string') {
    return new ResearchApiError({ kind: 'auth', message: detail })
  }
  if (status === 502 && typeof detail === 'string') {
    return new ResearchApiError({ kind: 'upstream', message: detail })
  }
  if (status === 500 && typeof detail === 'string') {
    return new ResearchApiError({ kind: 'server', message: detail })
  }

  return new ResearchApiError({
    kind: 'unknown',
    message: 'Something went wrong. Please try again.',
  })
}

export function parseExplainWidgetStartResponse(raw: unknown): { jobId: string } {
  if (!isRecord(raw) || typeof raw.job_id !== 'string' || raw.job_id === '') {
    throw new ResearchApiError({
      kind: 'unknown',
      message: 'Malformed response starting the research job.',
    })
  }
  return { jobId: raw.job_id }
}

function parseCost(raw: unknown): ResearchJobCost {
  const rec = isRecord(raw) ? raw : {}
  return {
    inputTokens: typeof rec.input_tokens === 'number' ? rec.input_tokens : 0,
    outputTokens: typeof rec.output_tokens === 'number' ? rec.output_tokens : 0,
    toolCalls: typeof rec.tool_calls === 'number' ? rec.tool_calls : 0,
  }
}

// The progress timeline added alongside ResearchJobStatus — absent or empty is normal (both
// event kinds are flag-gated backend-side), never evidence of a stalled job.
function parseEvents(raw: unknown): ResearchJobEvent[] {
  return Array.isArray(raw) ? raw.map((e) => ResearchJobEvent.fromJSON(e)) : []
}

// The wire's ReportBlock oneof is flattened as {"type": "markdown"|"chart", ...fields} —
// NOT the nested {markdown:{...}} / {chart:{...}} shape ReportBlock.fromJSON expects. Wrap
// the flat object under the right oneof key first, then let the generated parser read the
// member fields off it (the extra "type" key is simply ignored).
function parseReportBlock(raw: unknown): ReportBlock {
  if (!isRecord(raw)) return {}
  if (raw.type === 'markdown') return ReportBlock.fromJSON({ markdown: raw })
  if (raw.type === 'chart') return ReportBlock.fromJSON({ chart: raw })
  return {}
}

// The generated TimeRange.fromJSON only reads {start, end} — unlike every other message in
// this file it has no snake_case/alias fallback. The confirmed backend example delivers
// citations as time_range: {from, to}, not {start, end}, so Citation.fromJSON would silently
// parse an empty timeRange without this normalization. Tolerate both shapes.
function normalizeCitationTimeRange(raw: unknown): unknown {
  if (!isRecord(raw)) return raw
  const timeRange = raw.time_range ?? raw.timeRange
  if (!isRecord(timeRange)) return raw
  if (timeRange.start !== undefined || timeRange.end !== undefined) return raw
  return {
    ...raw,
    time_range: { start: timeRange.from, end: timeRange.to },
  }
}

// Preserves the original wire order (markdown and chart blocks are interleaved for narrative
// flow — dropping chart blocks, as this used to, both loses the chart data and desyncs the
// remaining markdown from its intended reading order).
function toParsedReportBlock(block: ReportBlock): ParsedReportBlock | null {
  if (block.markdown) {
    const md = block.markdown
    return { kind: 'markdown', section: md.section, content: md.content, sources: md.sources }
  }
  if (block.chart) {
    const chart = block.chart
    return {
      kind: 'chart',
      section: chart.section,
      caption: chart.caption,
      chartType: chart.chartType,
      reference: chart.reference,
      integrity: chart.integrity,
      series: chart.series,
      aggregates: chart.aggregates,
      sources: chart.sources,
      isPreview: chart.isPreview,
      truncated: chart.truncated,
    }
  }
  return null
}

function parseReport(raw: unknown): ParsedReport {
  if (!isRecord(raw) || !isRecord(raw.meta)) {
    throw new ResearchApiError({
      kind: 'unknown',
      message: 'The research report was delivered in an unexpected format.',
    })
  }
  const meta = ReportMeta.fromJSON(raw.meta)
  const rawBlocks = Array.isArray(raw.blocks) ? raw.blocks.map(parseReportBlock) : []
  const blocks = rawBlocks
    .map(toParsedReportBlock)
    .filter((block): block is ParsedReportBlock => block !== null)
  const citations = Array.isArray(raw.citations)
    ? raw.citations.map((c) => Citation.fromJSON(normalizeCitationTimeRange(c)))
    : []
  return { meta, blocks, citations }
}

// GET /research/{job_id} — phase is one of exactly five UPPER_SNAKE values, no "status"
// field exists on the wire. report is only present on DELIVERED; error (string | null) only
// on FAILED/KILLED. An unrecognized phase is treated as an error, never silently coerced.
export function parseResearchPollResponse(raw: unknown): ResearchPollResult {
  if (!isRecord(raw) || typeof raw.job_id !== 'string' || raw.job_id === '') {
    throw new ResearchApiError({
      kind: 'unknown',
      message: 'Malformed research job status response.',
    })
  }

  const jobId = raw.job_id
  const cost = parseCost(raw.cost)
  const events = parseEvents(raw.events)
  const phase = raw.phase

  switch (phase) {
    case 'PENDING':
    case 'RUNNING':
      return { phase, jobId, cost, events }
    case 'DELIVERED':
      return { phase: 'DELIVERED', jobId, cost, events, report: parseReport(raw.report) }
    case 'FAILED':
    case 'KILLED':
    case 'CANCELLED':
      return {
        phase,
        jobId,
        cost,
        events,
        error: typeof raw.error === 'string' ? raw.error : null,
      }
    default:
      throw new ResearchApiError({
        kind: 'unknown',
        message: `Unrecognized research job phase: ${String(phase)}`,
      })
  }
}

const JSON_REQUEST_CONFIG = {
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  responseType: 'json' as const,
}

// Deliberately NOT `ExplainWidgetRequest.toJSON()` — the generated proto toJSON emits
// camelCase keys and stringified int64s (rangeStartMs: "123"), but the confirmed backend
// example is snake_case with numeric (not string) range fields:
//   {"widget": {"metric": "...", "currency": "...", "exchange": "...",
//               "range_start_ms": 0, "range_end_ms": 0}}
// metric/currency/exchange are single-word keys so camelCase vs snake_case doesn't matter
// for them, and the response envelope already demonstrates enums round-trip as the full
// proto3-JSON member-name string (e.g. ChartReference.exchange: "EXCHANGE_DERIBIT") — so
// analyticsTypeToJSON/exchangeToJSON here should be correct. range_start_ms/range_end_ms are
// the one part confirmed to differ from the generated toJSON and are fixed explicitly below.
// user_query is an additional, optional sibling field on the same POST /research/widget body —
// 1000-char cap. Normalized here (not in postExplainWidget) so the existing serializer unit
// tests can exercise the clamp/omit behavior directly. A whitespace-only query omits the key
// entirely (matches "no query" rather than sending an empty string); a real query's incidental
// leading/trailing whitespace is preserved by slicing the original string, not the trimmed one.
export function serializeExplainWidgetRequest(widget: WidgetContext, userQuery?: string): unknown {
  const normalizedQuery = userQuery?.trim() ? userQuery.slice(0, 1000) : undefined
  return {
    widget: {
      metric: analyticsTypeToJSON(widget.metric),
      currency: widget.currency,
      exchange: exchangeToJSON(widget.exchange),
      range_start_ms: Number(widget.rangeStartMs),
      range_end_ms: Number(widget.rangeEndMs),
    },
    ...(normalizedQuery ? { user_query: normalizedQuery } : {}),
  }
}

export async function postExplainWidget(
  widget: WidgetContext,
  userQuery?: string
): Promise<{ jobId: string }> {
  const { orderXAiClient, isDevEnv } = await getAiClient()
  if (isDevEnv) {
    throw new ResearchApiError({
      kind: 'unknown',
      message: 'Widget research is not available in dev mode.',
    })
  }
  try {
    const response = await orderXAiClient.post(
      '/research/widget',
      serializeExplainWidgetRequest(widget, userQuery),
      JSON_REQUEST_CONFIG
    )
    return parseExplainWidgetStartResponse(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

export async function getResearchReport(jobId: string): Promise<ResearchPollResult> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.get(`/research/${jobId}`, {
      headers: { Accept: 'application/json' },
      responseType: 'json',
    })
    return parseResearchPollResponse(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

// General free-text research — confirmed via a debug tool against the real backend
// (2026-07-24): the field is `question`, not `user_query` (that name 422s as
// `extra_forbidden` on this endpoint). Poll uses the exact same GET /research/{job_id} as
// /research/widget — parseResearchPollResponse is already correct for it, no changes needed.
export function serializeResearchQuestionRequest(question: string): unknown {
  return { question }
}

export async function postResearchQuestion(question: string): Promise<{ jobId: string }> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.post(
      '/research',
      serializeResearchQuestionRequest(question),
      JSON_REQUEST_CONFIG
    )
    return parseExplainWidgetStartResponse(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

export type ResearchFollowupMode = 'simple' | 'report'

// Confirmed via components/debug/research-debug-panel.tsx against the live backend
// (2026-07-28, see docs/log-followup-research.txt): the field is `question` (same as
// postResearchQuestion above, NOT userQuery like /research/widget's ExplainWidgetRequest).
// Poll uses the exact same GET /research/{job_id} as every other producer —
// parseResearchPollResponse already handles this response verbatim (parent_job_id,
// meta.use_case: "followup_research", disclaimers, citations all round-tripped correctly in
// the confirmed log), no parser changes needed.
export function serializeFollowupResearchRequest(question: string): unknown {
  return { question }
}

// `mode` is accepted but NOT YET sent: the confirmed log only exercised the data-refetching
// "follow-up report" path (a 43s multi-phase run with tool calls, meta.use_case:
// "followup_research") — sending nothing but `question` was enough to get that behavior. No
// flag/field distinguishing it from the cheap single-LLM-call "simple answer" mode has been
// confirmed yet, so today BOTH `mode` values produce the same (report) behavior. Once a flag is
// confirmed for the simple path, thread it into serializeFollowupResearchRequest instead of
// guessing a name here.
export async function postFollowupResearch(
  parentJobId: string,
  question: string,
  _mode: ResearchFollowupMode
): Promise<{ jobId: string }> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.post(
      `/research/${parentJobId}/followup-research`,
      serializeFollowupResearchRequest(question),
      JSON_REQUEST_CONFIG
    )
    return parseExplainWidgetStartResponse(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

export type ResearchCancelOutcome = 'accepted' | 'already_terminal' | 'not_cancellable'

export interface ResearchCancelResult {
  outcome: ResearchCancelOutcome
  jobId: string
  phase: ResearchJobPhase | null
}

// 202 body: {job_id, phase, cancel_requested: true}. 409 body: {code, job_id, phase} — code is
// "already_terminal" or "not_cancellable_here". Either way the phase itself is read off the
// body only for logging/debugging; the real terminal CANCELLED phase is only ever learned by
// the normal GET /research/{job_id} poll, per the confirmed contract.
function parseCancelResponse(
  status: number,
  raw: unknown,
  fallbackJobId: string
): ResearchCancelResult {
  const rec = isRecord(raw) ? raw : {}
  const jobId = typeof rec.job_id === 'string' ? rec.job_id : fallbackJobId
  const phase = typeof rec.phase === 'string' ? (rec.phase as ResearchJobPhase) : null
  if (status === 202) {
    return { outcome: 'accepted', jobId, phase }
  }
  return {
    outcome: rec.code === 'already_terminal' ? 'already_terminal' : 'not_cancellable',
    jobId,
    phase,
  }
}

// POST /research/{job_id}/cancel — bearer auth, no body. 202 means accepted-not-finished: the
// job reaches CANCELLED asynchronously, learned by the existing poll loop. 409 is not a
// failure — it's the server distinguishing "already done" from "can't be stopped from here";
// branch on `code`, never the HTTP status alone. A double-press is safe (the second press just
// comes back already_terminal). 404 (unknown job id, or what a flag-off deployment answers) and
// 401 fall through to parseResearchApiError like every other /research route.
export async function cancelResearchJob(jobId: string): Promise<ResearchCancelResult> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.post(`/research/${jobId}/cancel`, undefined, {
      ...JSON_REQUEST_CONFIG,
      validateStatus: (status: number) => status === 202 || status === 409,
    })
    return parseCancelResponse(response.status, response.data, jobId)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

// POST /research/classify
// flag.ts's RESEARCH_CLASSIFY_ENABLED) ──────────────────────────────────────────────────────
//
// The classifier is the single entry point for conversational research. It either starts a
// pollable job itself, returns a direct answer, or returns user-facing clarify/refuse copy.

export interface ResearchProposal {
  proposalId: string
  summary?: string
  topics?: string[]
  instruments?: string[]
  window?: string
  expiresAt?: string
  // Only meaningful for FOLLOWUP_ON_EXISTING — the job/report this proposal would refresh.
  // Field name AND presence are unconfirmed (plan blocker #1); read from the likely candidate
  // keys and left undefined otherwise. startProposedFollowup throws a clear, specific error if
  // this is missing when a followup is actually confirmed, rather than silently no-op'ing.
  parentJobId?: string
}

export interface ResearchAnswer {
  text: string
  sources?: string[]
}

export type ResearchClassifyResult =
  | {
      type: 'job'
      threadId: string
      jobId: string
      requestId?: string
      parentJobId?: string
    }
  | { type: 'answer'; threadId: string; requestId?: string; answer: ResearchAnswer }
  | {
      type: 'message'
      threadId: string
      requestId?: string
      message: string
      reason?: 'clarify' | 'refuse'
    }

function requireString(rec: Record<string, unknown>, key: string, context: string): string {
  const value = rec[key]
  if (typeof value !== 'string' || value === '') {
    throw new ResearchApiError({
      kind: 'unknown',
      message: `Malformed research classify response: missing "${key}" for ${context}.`,
    })
  }
  return value
}

function optionalString(rec: Record<string, unknown>, key: string): string | undefined {
  return typeof rec[key] === 'string' ? (rec[key] as string) : undefined
}

function optionalStringArray(rec: Record<string, unknown>, key: string): string[] | undefined {
  const value = rec[key]
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : undefined
}

export function parseResearchClassifyResponse(raw: unknown): ResearchClassifyResult {
  if (!isRecord(raw)) {
    throw new ResearchApiError({
      kind: 'unknown',
      message: 'Malformed research classify response.',
    })
  }

  const route = optionalString(raw, 'route') ?? optionalString(raw, 'type')
  const threadId = requireString(raw, 'thread_id', route ?? 'response')
  const requestId = optionalString(raw, 'request_id')
  const job = isRecord(raw.run)
    ? raw.run
    : isRecord(raw.followup)
      ? raw.followup
      : isRecord(raw.job)
        ? raw.job
        : raw
  const jobId = optionalString(job, 'job_id')

  if (jobId) {
    return {
      type: 'job',
      threadId,
      jobId,
      requestId,
      parentJobId: optionalString(job, 'parent_job_id') ?? optionalString(raw, 'parent_job_id'),
    }
  }

  const answer = isRecord(raw.answer) ? raw.answer : raw
  const answerText =
    optionalString(answer, 'text') ??
    (route === 'DIRECT_ANSWER' ? optionalString(raw, 'message') : undefined)
  if (route === 'DIRECT_ANSWER' || route === 'ANSWER' || route === 'answer') {
    if (!answerText) {
      throw new ResearchApiError({
        kind: 'unknown',
        message: 'Malformed research classify response: missing direct-answer text.',
      })
    }
    return {
      type: 'answer',
      threadId,
      requestId,
      answer: { text: answerText, sources: optionalStringArray(answer, 'sources') },
    }
  }

  const userMessage = optionalString(raw, 'user_message') ?? optionalString(raw, 'message')
  if (route === 'CLARIFY' || route === 'REFUSE' || route === 'MESSAGE' || route === 'message') {
    if (!userMessage) {
      throw new ResearchApiError({
        kind: 'unknown',
        message: 'Malformed research classify response: missing user-facing message.',
      })
    }
    return {
      type: 'message',
      threadId,
      requestId,
      message: userMessage,
      reason: route === 'CLARIFY' ? 'clarify' : route === 'REFUSE' ? 'refuse' : undefined,
    }
  }

  throw new ResearchApiError({
    kind: 'unknown',
    message: `Unrecognized research classify response: ${String(route)}`,
  })
}

export interface ClassifyResearchMessageOptions {
  threadId?: string
}

export function serializeResearchClassifyRequest(
  message: string,
  options: ClassifyResearchMessageOptions
): unknown {
  return {
    message,
    thread_id: options.threadId ?? null,
  }
}

export function isResearchThreadCreationFailure(raw: unknown): boolean {
  if (!isRecord(raw)) return false
  if (raw.thread_created === false || raw.thread_creation_failed === true) return true
  const nested = isRecord(raw.detail) ? raw.detail : isRecord(raw.error) ? raw.error : undefined
  const code = (
    optionalString(raw, 'code') ??
    optionalString(raw, 'route') ??
    optionalString(raw, 'type') ??
    optionalString(nested ?? {}, 'code')
  )?.toLowerCase()
  if (
    code === 'thread_creation_failed' ||
    code === 'thread_create_failed' ||
    code === 'could_not_create_thread'
  ) {
    return true
  }

  const detail =
    optionalString(raw, 'detail') ??
    optionalString(raw, 'message') ??
    optionalString(nested ?? {}, 'message')
  return (
    !!detail &&
    /thread/i.test(detail) &&
    /creat/i.test(detail) &&
    /(could not|unable|fail)/i.test(detail)
  )
}

export async function classifyResearchMessage(
  message: string,
  options: ClassifyResearchMessageOptions
): Promise<ResearchClassifyResult> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.post(
      '/research/classify',
      serializeResearchClassifyRequest(message, options),
      JSON_REQUEST_CONFIG
    )
    if (isResearchThreadCreationFailure(response.data)) {
      const { jobId } = await postResearchQuestion(message)
      return { type: 'job', threadId: options.threadId ?? '', jobId }
    }
    return parseResearchClassifyResponse(response.data)
  } catch (error) {
    if (axios.isAxiosError(error) && isResearchThreadCreationFailure(error.response?.data)) {
      const { jobId } = await postResearchQuestion(message)
      return { type: 'job', threadId: options.threadId ?? '', jobId }
    }
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

// ─── Proposal-aware POST /research and POST /research/{id}/followup ───────────────────────
//
// Additive, NOT a replacement for postResearchQuestion/postFollowupResearch above — those keep
// serving flag-off clients unchanged. `startProposedFollowup`'s path/body is provisional
// pending plan blocker #1 (the confirmed contract may turn out to need no separate
// `parentJobId` at all, if the backend accepts `proposal_id` alone).

export function serializeProposedResearchRequest(question: string, proposalId: string): unknown {
  return { question, proposal_id: proposalId }
}

export async function startProposedResearch(params: {
  question: string
  proposalId: string
}): Promise<{ jobId: string }> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.post(
      '/research',
      serializeProposedResearchRequest(params.question, params.proposalId),
      JSON_REQUEST_CONFIG
    )
    return parseExplainWidgetStartResponse(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

export function serializeProposedFollowupRequest(question: string, proposalId: string): unknown {
  return { question, proposal_id: proposalId }
}

export async function startProposedFollowup(params: {
  parentJobId: string
  question: string
  proposalId: string
  mode: ResearchFollowupMode
}): Promise<{ jobId: string }> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.post(
      `/research/${params.parentJobId}/followup`,
      serializeProposedFollowupRequest(params.question, params.proposalId),
      JSON_REQUEST_CONFIG
    )
    return parseExplainWidgetStartResponse(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

// ─── Thread history: GET /research/threads and GET /research/threads/{thread_id} ──────────
//
// The server is the only record of past conversations (IndexedDB persistence was retired).
// Both lists are cursor-paged: pass the previous page's `next_cursor`; it is absent on the last
// page. A thread owned by someone else is a 404.

export const RESEARCH_THREAD_SEARCH_MAX_LENGTH = 80

export interface ResearchThreadSummary {
  threadId: string
  title: string
  createdAt: number
  updatedAt: number
  turnCount: number
  lastRoute: string | null
}

export interface ResearchThreadPage {
  threads: ResearchThreadSummary[]
  nextCursor?: string
}

export interface ResearchThreadTurn {
  seq: number
  role: 'user' | 'assistant'
  text: string
  /** Set on a turn that started (or points at) a research run; its report lives on the poll. */
  jobId: string | null
  route: string | null
  createdAt: number
}

export interface ResearchThreadDetail {
  thread: ResearchThreadSummary
  turns: ResearchThreadTurn[]
  nextCursor?: string
}

function parseTimestamp(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value !== 'string') return 0
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

function parseNextCursor(raw: Record<string, unknown>): string | undefined {
  return typeof raw.next_cursor === 'string' && raw.next_cursor ? raw.next_cursor : undefined
}

export function parseResearchThreadSummary(raw: unknown): ResearchThreadSummary | null {
  if (!isRecord(raw) || typeof raw.thread_id !== 'string' || !raw.thread_id) return null
  return {
    threadId: raw.thread_id,
    title: optionalString(raw, 'title') ?? '',
    createdAt: parseTimestamp(raw.created_at),
    updatedAt: parseTimestamp(raw.updated_at ?? raw.created_at),
    turnCount: typeof raw.turn_count === 'number' ? raw.turn_count : 0,
    lastRoute: optionalString(raw, 'last_route') ?? null,
  }
}

export function parseResearchThreadPage(raw: unknown): ResearchThreadPage {
  if (!isRecord(raw) || !Array.isArray(raw.threads)) {
    throw new ResearchApiError({ kind: 'unknown', message: 'Malformed research history.' })
  }
  return {
    threads: raw.threads
      .map(parseResearchThreadSummary)
      .filter((thread): thread is ResearchThreadSummary => thread !== null),
    nextCursor: parseNextCursor(raw),
  }
}

function firstString(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === 'string' && value !== '')
}

const USER_ROLE = /\b(user|human|client|question|request|inbound)\b/i
const ASSISTANT_ROLE =
  /\b(assistant|ai|bot|model|system|reply|answer|proposal|run|clarify|refuse)\b/i

// The turn schema isn't contracted in proto yet, so this reads the likely spellings. The speaker
// comes from whichever role-ish key the turn has; without one, a turn that carries a routing
// outcome (route, job, answer, proposal) is the assistant's and anything else is the user's.
export function parseResearchThreadTurn(raw: unknown, index: number): ResearchThreadTurn | null {
  if (!isRecord(raw)) return null
  const roleHint = firstString(
    raw.role,
    raw.author,
    raw.speaker,
    raw.sender,
    raw.from,
    raw.actor,
    raw.kind,
    raw.type
  )?.replace(/_/g, ' ')
  const run = isRecord(raw.run) ? raw.run : {}
  const answer = isRecord(raw.answer) ? raw.answer : {}
  const jobId = firstString(raw.job_id, run.job_id) ?? null
  const route = firstString(raw.route) ?? null
  const carriesOutcome = !!(jobId || route || raw.answer || raw.proposal || raw.run)
  const role: ResearchThreadTurn['role'] =
    roleHint && USER_ROLE.test(roleHint)
      ? 'user'
      : roleHint && ASSISTANT_ROLE.test(roleHint)
        ? 'assistant'
        : carriesOutcome
          ? 'assistant'
          : 'user'
  return {
    seq: typeof raw.seq === 'number' ? raw.seq : index,
    role,
    text:
      firstString(
        raw.text,
        raw.content,
        raw.message,
        role === 'user' ? raw.question : undefined,
        raw.user_message,
        answer.text
      ) ?? '',
    jobId,
    route,
    createdAt: parseTimestamp(raw.created_at),
  }
}

export function parseResearchThreadDetail(raw: unknown): ResearchThreadDetail {
  const thread = isRecord(raw) ? parseResearchThreadSummary(raw.thread) : null
  if (!isRecord(raw) || !thread || !Array.isArray(raw.turns)) {
    throw new ResearchApiError({ kind: 'unknown', message: 'Malformed research thread.' })
  }
  if (process.env.NODE_ENV !== 'production') {
    // Uncontracted shape (see parseResearchThreadTurn): keep the raw turns inspectable.
    console.debug('[research] thread turns (raw)', raw.turns)
  }
  return {
    thread,
    turns: raw.turns
      .map(parseResearchThreadTurn)
      .filter((turn): turn is ResearchThreadTurn => turn !== null),
    nextCursor: parseNextCursor(raw),
  }
}

export async function listResearchThreads(params: {
  limit?: number
  cursor?: string
  q?: string
}): Promise<ResearchThreadPage> {
  const { orderXAiClient } = await getAiClient()
  // A blank or over-long `q` is a 422, so it's dropped/clipped here rather than sent.
  const q = params.q?.trim().slice(0, RESEARCH_THREAD_SEARCH_MAX_LENGTH)
  try {
    const response = await orderXAiClient.get('/research/threads', {
      params: {
        limit: params.limit,
        ...(params.cursor ? { cursor: params.cursor } : {}),
        ...(q ? { q } : {}),
      },
      headers: { Accept: 'application/json' },
      responseType: 'json',
    })
    return parseResearchThreadPage(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

export async function getResearchThread(
  threadId: string,
  params: { limit?: number; cursor?: string } = {}
): Promise<ResearchThreadDetail> {
  const { orderXAiClient } = await getAiClient()
  try {
    const response = await orderXAiClient.get(`/research/threads/${encodeURIComponent(threadId)}`, {
      params: {
        limit: params.limit,
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
      headers: { Accept: 'application/json' },
      responseType: 'json',
    })
    return parseResearchThreadDetail(response.data)
  } catch (error) {
    if (error instanceof ResearchApiError) throw error
    throw parseResearchApiError(error)
  }
}

/** Every turn of a thread, following the cursor to the end (turns arrive oldest first). */
export async function getAllResearchThreadTurns(threadId: string): Promise<ResearchThreadDetail> {
  const first = await getResearchThread(threadId, { limit: 100 })
  const turns = [...first.turns]
  let cursor = first.nextCursor
  while (cursor) {
    const page = await getResearchThread(threadId, { limit: 100, cursor })
    turns.push(...page.turns)
    cursor = page.nextCursor
  }
  return { thread: first.thread, turns: turns.sort((a, b) => a.seq - b.seq) }
}
